import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Save, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import {
  emptyVisualCue,
  triggerWordOptions,
} from '@/lib/scriptAudioAlignVisuals';
import {
  ARTIFACT_CATEGORIES,
  artifactCategoryLabel,
  artifactKind,
  artifactLabel,
} from '@/lib/scriptAudioAlignArtifacts';

import { TwinneeLogo } from '@/components/TwinneeLogo';
function CueRow({
  cue,
  sentences,
  onChange,
  onRemove,
}) {
  const sentenceOptions = (sentences || []).map((s) => ({
    value: String(s.index),
    label: `[${s.index}] ${s.text.slice(0, 60)}${s.text.length > 60 ? '…' : ''}`,
  }));

  const selectedIndices = (cue.sentence_indices || []).map(String);
  const primarySentence = selectedIndices[0] != null ? Number(selectedIndices[0]) : 0;
  const wordOpts = triggerWordOptions(sentences, cue.sentence_indices?.length
    ? cue.sentence_indices
    : [primarySentence]);

  const generatorLabel = artifactCategoryLabel(cue.visual_type);
  const kind = artifactKind(cue.visual_type);

  const handleSentenceChange = (value) => {
    const idx = Number(value);
    onChange({ ...cue, sentence_indices: [idx] });
  };

  return (
    <div className="rounded-lg border border-slate-700/70 bg-slate-900/40 p-3 space-y-3">
      <div className="flex flex-wrap gap-2 items-start justify-between">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 flex-1">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Type</Label>
            <Select
              value={cue.visual_type || 'explanation'}
              onValueChange={(v) => onChange({ ...cue, visual_type: v })}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80">
                {ARTIFACT_CATEGORIES.map((group) => (
                  <SelectGroup key={group.category}>
                    <SelectLabel className="text-[10px] uppercase text-slate-500">
                      {group.label}
                    </SelectLabel>
                    {group.types.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {generatorLabel ? (
              <p className="text-[10px] text-slate-500 leading-tight">
                <span className="text-violet-300/90">{generatorLabel} generator</span>
                {' → '}
                {artifactLabel(cue.visual_type)}
                {kind ? <span className="text-slate-600"> · {kind}</span> : null}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Sentence</Label>
            <Select value={String(primarySentence)} onValueChange={handleSentenceChange}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sentenceOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Trigger word</Label>
            <Select
              value={cue.trigger_word || ''}
              onValueChange={(v) => onChange({ ...cue, trigger_word: v })}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick word" /></SelectTrigger>
              <SelectContent>
                {wordOpts.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500">Status</Label>
            <p className="text-xs text-slate-400 h-8 flex items-center">{cue.status || '—'}</p>
          </div>
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-red-400 h-8" onClick={onRemove}>
          Remove
        </Button>
      </div>

      <div className="space-y-1">
        <Label className="text-[10px] uppercase text-slate-500">Heading</Label>
        <Input
          className="h-8 text-sm"
          value={cue.heading || ''}
          onChange={(e) => onChange({ ...cue, heading: e.target.value })}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-[10px] uppercase text-slate-500">Content</Label>
        <Textarea
          className="text-sm min-h-[72px]"
          value={cue.content || ''}
          onChange={(e) => onChange({ ...cue, content: e.target.value })}
          placeholder={
            cue.visual_type === 'flowchart'
              ? 'Mermaid graph TD or step1; step2; step3'
              : cue.visual_type === 'hierarchy_tree'
                ? 'Indented outline or JSON nodes'
                : 'Body text, scenario, or image prompt'
          }
        />
      </div>

      {cue.suitability_reason ? (
        <p className="text-[11px] text-violet-300/80">{cue.suitability_reason}</p>
      ) : null}
    </div>
  );
}

export function VisualPlanEditor({
  jobId,
  result,
  onResultUpdate,
}) {
  const [plan, setPlan] = useState(() => result?.visual_plan || []);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);

  const sentences = useMemo(() => result?.sentences || [], [result?.sentences]);

  useEffect(() => {
    if (Array.isArray(result?.visual_plan) && result.visual_plan.length) {
      setPlan(result.visual_plan);
    }
  }, [result?.visual_plan]);

  const updateCue = (index, next) => {
    setPlan((prev) => prev.map((c, i) => (i === index ? next : c)));
  };

  const removeCue = (index) => {
    setPlan((prev) => prev.filter((_, i) => i !== index));
  };

  const addCue = () => {
    setPlan((prev) => [...prev, emptyVisualCue(sentences[0]?.index ?? 0)]);
  };

  const handleSuggest = async () => {
    if (!jobId) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/script-audio-align/jobs/${jobId}/visual-plan/suggest`, {}, { timeout: 120_000 });
      setPlan(data.visual_plan || []);
      onResultUpdate?.(data);
      toast.success('Visual plan suggested');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Suggest failed');
    } finally {
      setSuggesting(false);
    }
  };

  const handleSave = async () => {
    if (!jobId) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/script-audio-align/jobs/${jobId}/visual-plan`, {
        visual_plan: plan,
      });
      onResultUpdate?.(data);
      toast.success('Visual plan saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBuildPreview = async () => {
    if (!jobId) return;
    setBuilding(true);
    try {
      await api.put(`/script-audio-align/jobs/${jobId}/visual-plan`, { visual_plan: plan });
      const { data } = await api.post(`/script-audio-align/jobs/${jobId}/visual-preview/build`, {}, { timeout: 60_000 });
      setPlan(data.visual_plan || plan);
      onResultUpdate?.(data);
      toast.success('Preview ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Build preview failed');
    } finally {
      setBuilding(false);
    }
  };

  if (!jobId || result?.status !== 'completed') return null;

  return (
    <div className="space-y-4 rounded-2xl border p-5" style={{ borderColor: 'var(--glass-border)' }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <TwinneeLogo className="w-5 h-5 text-violet-400" />
            Word-synced visuals
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">
            LLM suggests visuals; edit trigger words and content, then build preview.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleSuggest} disabled={suggesting}>
            {suggesting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
            Suggest visuals
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleSave} disabled={saving || !plan.length}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save plan
          </Button>
          <Button type="button" size="sm" onClick={handleBuildPreview} disabled={building || !plan.length}>
            {building ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Build preview
          </Button>
        </div>
      </div>

      {plan.length === 0 ? (
        <p className="text-sm text-slate-500">No visual cues yet. Click &quot;Suggest visuals&quot; or add one manually.</p>
      ) : (
        <div className="space-y-3">
          {plan.map((cue, i) => (
            <CueRow
              key={cue.cue_id || i}
              cue={cue}
              sentences={sentences}
              onChange={(next) => updateCue(i, next)}
              onRemove={() => removeCue(i)}
            />
          ))}
        </div>
      )}

      <Button type="button" variant="ghost" size="sm" onClick={addCue}>
        <Plus className="w-4 h-4 mr-1" />
        Add cue
      </Button>
    </div>
  );
}
